import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, router } from '@inertiajs/react';
import { Copyright } from 'lucide-react';
import PrinciplesCarousel from '../../components/landing/PrinciplesCarousel';
import CourseShowcase from '../../components/landing/CourseShowcase';
import Logo from '../../components/ui/Logo';

export default function Login({ error, email: initialEmail }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState(initialEmail || '');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  // Auto-open dropdown if there's a server error
  useEffect(() => {
    if (error) setShowLogin(true);
  }, [error]);

  // Close on click outside
  useEffect(() => {
    if (!showLogin) return;
    const handler = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target)
      ) {
        setShowLogin(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showLogin]);

  // Close on Escape
  useEffect(() => {
    if (!showLogin) return;
    const handler = (e) => {
      if (e.key === 'Escape') setShowLogin(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [showLogin]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    router.post('/login', { email, password }, {
      onFinish: () => setIsSubmitting(false),
    });
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex flex-col text-[#FAFAFA]">
      {/* Top nav */}
      <nav className="relative z-10 flex items-center justify-between px-6 py-5 sm:px-10">
        <Link href="/" className="no-underline">
          <Logo className="h-8" forceDark />
        </Link>

        <div className="flex items-center gap-4">
          <Link
            href="/register"
            className="text-sm text-[#888] hover:text-white transition-colors no-underline"
            style={{ fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace", letterSpacing: '0.02em' }}
          >
            {t('auth.register')}
          </Link>

          <div className="relative">
            <button
              ref={buttonRef}
              data-testid="login-open"
              onClick={() => setShowLogin(!showLogin)}
              className="text-sm px-4 py-2 rounded-full border border-[#333] bg-transparent text-[#ccc] hover:border-[#666] hover:text-white transition-all cursor-pointer"
              style={{ fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace", letterSpacing: '0.02em' }}
            >
              {t('auth.signIn')}
            </button>

            {/* Dropdown login card */}
            {showLogin && (
              <div
                ref={dropdownRef}
                className="absolute right-0 top-full mt-3 w-72 rounded-xl border border-[#222] bg-[#111] shadow-2xl p-5"
                style={{ animation: 'dropdownIn 0.2s ease' }}
              >
                <form onSubmit={handleSubmit} data-testid="login-form" className="flex flex-col gap-3">
                  {error && (
                    <div data-testid="login-error" className="px-3 py-2 rounded-lg bg-red-900/30 border border-red-800/50">
                      <p className="text-xs text-red-400">{error}</p>
                    </div>
                  )}

                  <input
                    type="email"
                    data-testid="login-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('auth.email')}
                    autoComplete="email"
                    autoFocus
                    required
                    className="w-full px-3 py-2 rounded-lg bg-[#1a1a1a] border border-[#333] text-sm text-[#eee] placeholder-[#555] focus:outline-none focus:border-[#555] transition-colors"
                  />

                  <input
                    type="password"
                    data-testid="login-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('auth.password')}
                    autoComplete="current-password"
                    required
                    className="w-full px-3 py-2 rounded-lg bg-[#1a1a1a] border border-[#333] text-sm text-[#eee] placeholder-[#555] focus:outline-none focus:border-[#555] transition-colors"
                  />

                  <button
                    type="submit"
                    data-testid="login-submit"
                    disabled={isSubmitting}
                    className="w-full py-2 rounded-lg bg-[#FAFAFA] text-[#0A0A0A] text-sm font-medium cursor-pointer hover:bg-[#e0e0e0] transition-colors disabled:opacity-50 disabled:cursor-not-allowed border-none"
                  >
                    {isSubmitting ? t('auth.signingIn') : t('auth.signIn')}
                  </button>
                </form>

                <div className="mt-3 pt-3 border-t border-[#222] text-center">
                  <Link
                    href="/join"
                    className="text-xs text-[#666] hover:text-[#aaa] transition-colors no-underline"
                  >
                    {t('auth.studentJoinWithCode')}
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Carousel hero */}
      <div className="min-h-[calc(100vh-72px)] flex items-center justify-center px-6">
        <PrinciplesCarousel />
      </div>

      {/* Course showcase */}
      <CourseShowcase />

      {/* Footer */}
      <footer className="py-6 text-center space-y-2">
        <div className="flex items-center justify-center gap-4 text-xs text-[#555]">
          <Link href="/privacy" className="hover:text-[#aaa] transition-colors no-underline">Privacy</Link>
          <span>·</span>
          <Link href="/terms" className="hover:text-[#aaa] transition-colors no-underline">Terms</Link>
        </div>
        <p className="inline-flex items-center gap-1 text-xs text-[#555]">
          <Copyright className="w-3.5 h-3.5" />
          {t('common.studyfitInc')}
        </p>
      </footer>

      {/* Dropdown animation */}
      <style>{`
        @keyframes dropdownIn {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}
